import { useEffect } from "react";
import { Link } from "react-router-dom";
import { setJsonLd, setPageMeta } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Motion";
import { SectionVideo } from "@/components/SectionVideo";
import { SHOP_META, SHOP_PRODUCTS, TOP_COLLECTIONS, CITY_STATE_GROUPS } from "@/lib/shopCatalog";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { OfferCapture } from "@/components/OfferCapture";
import { ExitIntentOffer } from "@/components/ExitIntentOffer";
import { trackViewItemList } from "@/lib/analytics";

const colLabel = (c) => c.slug.replace(/-/g, " ").toUpperCase();

export default function Shop() {
  useEffect(() => {
    setPageMeta(SHOP_META.title, SHOP_META.description, { canonicalPath: "/shop" });
    trackViewItemList(SHOP_PRODUCTS, "shop", "Shop");
    return setJsonLd("shop-jsonld", SHOP_META.jsonld);
  }, []);

  return (
    <div className="bg-night min-h-screen">
      <header className="relative overflow-hidden px-5 lg:px-10 pt-16 pb-10 border-b border-white/10">
        <SectionVideo src="/video/run-12.mp4" srcMobile="/video/run-12-mobile.mp4" poster="/video/run-12-poster.jpg" opacity={0.22} testid="shop-hero-video" />
        <div className="relative">
        <Reveal>
          <p className="font-mono-label text-white/40">THE SHOP</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none uppercase">
            Running Apparel &amp; City Series Shirts
          </h1>
          <p className="font-display text-xl lg:text-2xl text-white/40 mt-4">EARNED, NOT BOUGHT.</p>
          <p data-testid="shop-direct-answer" className="text-white/60 text-sm max-w-2xl mt-5 leading-relaxed">
            {SHOP_META.direct_answer}
          </p>
        </Reveal>
        </div>
      </header>

      <nav
        aria-label="Shop collections"
        data-testid="shop-collection-filters"
        className="sticky top-16 z-40 bg-night/90 backdrop-blur-xl border-b border-white/10 px-5 lg:px-10 py-4 flex gap-2 overflow-x-auto"
      >
        <span className="font-mono-label px-4 py-2.5 border bg-white text-ink border-white whitespace-nowrap">ALL</span>
        {TOP_COLLECTIONS.map((c) => (
          <Link
            key={c.slug}
            to={c.path}
            data-testid={`shop-collection-${c.slug}`}
            className="font-mono-label px-4 py-2.5 border whitespace-nowrap border-white/20 text-white/60 hover:border-white transition-colors duration-200"
          >
            {colLabel(c)}
          </Link>
        ))}
      </nav>

      <div className="px-5 lg:px-10 py-5 border-b border-white/10 flex gap-2 flex-wrap items-center" data-testid="shop-city-links">
        <span className="font-mono-label text-white/30 mr-2">SHOP BY STATE</span>
        {CITY_STATE_GROUPS.map((g) => (
          <Link
            key={g.slug}
            to={`/shop/state/${g.slug}`}
            data-testid={`shop-state-${g.slug}`}
            className="font-mono-label border border-white/10 px-3 py-2 text-white/50 hover:bg-white hover:text-ink transition-colors duration-200"
          >
            {g.state.toUpperCase()} ({g.cities.length})
          </Link>
        ))}
      </div>

      <RecentlyViewed className="px-5 lg:px-10 py-12 border-b border-white/10" />

      <div className="px-5 lg:px-10 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="shop-grid">
          {SHOP_PRODUCTS.map((p) => (
            <ProductCard key={p.slug} p={p} listId="shop" listName="Shop" />
          ))}
        </div>

        <div className="mt-12">
          <OfferCapture source="shop" />
        </div>
        <ExitIntentOffer source="exit-shop" />

        <div className="border border-white/15 mt-12 p-8 lg:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="font-display text-2xl lg:text-3xl">WANT YOUR CLUB ON A SHIRT?</p>
            <p className="text-white/50 text-sm mt-2">Co-branded drops with design, production, and fulfillment handled. Revenue share included.</p>
          </div>
          <Link to="/collabs" data-testid="shop-collab-cta" className="btn-invert bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white shrink-0">
            BUILD A COLLAB
          </Link>
        </div>

        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2" data-testid="shop-policy-links">
          {[["SHIPPING", "/shipping"], ["RETURNS", "/returns"], ["SIZE GUIDE", "/size-guide"], ["ORDER LOOKUP", "/order-lookup"], ["CONTACT", "/contact"]].map(([label, to]) => (
            <Link key={to} to={to} className="font-mono-label text-white/40 hover:text-white link-underline transition-colors duration-200">
              {label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
